import CameraIcon from '@mui/icons-material/Camera';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@sophocles/features/toast';
import { DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import useCommandDispatch from '@sophocles/hooks/useCommandDispatch';

function CameraPicturePopup({ site, camera, isStandAlone }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const siteId = site.get('id', 0);
  const { dispatchCommands, submitting, activityLocked } = useCommandDispatch(siteId);

  const handleTouchTap = event => {
    // This prevents ghost click.
    event.preventDefault();
    if (activityLocked) return;
    setAnchorEl(event.currentTarget);
  };

  const handleRequestClose = () => setAnchorEl(null);

  const showFailure = () =>
    dispatch(
      addToast({
        title: t('general.error'),
        message: t('media.imageRequestFailed'),
        level: 'error',
        dismissible: true,
      }),
    );

  const takePicture = () => {
    if (submitting || activityLocked) return;

    const cameraNumber = camera.camera_number ?? 0;
    // Standalone CIC uses CIC_IMAGE; addon camera uses CAMERA.
    const commandKey = isStandAlone ? 'CIC_IMAGE' : 'CAMERA';

    if (siteId !== 0 && cameraNumber !== 0) {
      dispatchCommands([{ key: commandKey, params: { camera_number: cameraNumber } }]).then(result => {
        if (result.status === DISPATCH_STATUS.SUCCESS) {
          dispatch(
            addToast({
              title: t('media.imageRequested'),
              message: t('media.imageRequestedMessage', { cameraNumber }),
              level: 'info',
              dismissible: true,
            }),
          );
        } else {
          showFailure();
        }
        handleRequestClose();
      });
    } else {
      showFailure();
      handleRequestClose();
    }
  };

  return (
    <div>
      <IconButton disabled={submitting || activityLocked} onClick={handleTouchTap}>
        <Icon className="fa fa-camera" fontSize="18px" sx={{ color: 'common.white' }} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        targetOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={handleRequestClose}
      >
        <div
          style={{
            width: '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 10px 10px',
            fontSize: '14px',
            color: 'var(--text-secondary)',
          }}
        >
          <div style={{ lineHeight: '36px' }}>
            <span>{t('media.imageCapture')}</span>
            <span
              className="far fa-window-close"
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                cursor: 'pointer',
                padding: '12px',
              }}
              onClick={handleRequestClose}
            />
          </div>
          <Divider sx={{ width: '100%', mb: 1 }} />
          <div style={{ marginBottom: '8px', fontSize: '14px', textAlign: 'center' }}>
            {t('media.takePictureDescription')}
          </div>
          <Button
            variant="contained"
            color="success"
            disabled={submitting || activityLocked}
            startIcon={submitting ? <CircularProgress color="inherit" size={16} /> : <CameraIcon />}
            onClick={takePicture}
          >
            {submitting ? t('commandCatalog.sending') : t('media.takePicture')}
          </Button>
        </div>
      </Popover>
    </div>
  );
}

export default CameraPicturePopup;
